import { ValidationControlBase } from "@frui.ts/bootstrap";
import { bound } from "@frui.ts/helpers";
import { IBindingProps } from "@frui.ts/views";
import React from "react";
import { Col, Form } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import Tooltip from "controls/tooltip";
import { action, observable } from "mobx";
import LicensePlateCharacterWiseInput from "controls/licensePlateCharacterWiseInput";
import LicensePlateBaseInput from "controls/licensePlateBaseInput/licensePlateBaseInput";

export interface LicensePlateInputProps {
  id: string;
  translate: (code: string) => string;
  locale: string;
  countryId?: string;
  countryCode?: string;
  originalLicensePlate?: string;
  disabled?: boolean;
  errorMessage?: string;
  tooltip?: boolean;
  disableCopy?: boolean;
  disablePaste?: boolean;
  breakpoint?: "xl" | "xxl";
  onBlur?: (e: React.FormEvent<any>) => void;
  showTooltip?: boolean;
  fixedLength?: boolean;
  maxLength?: number;
}

export default class LicensePlateInput<TTarget> extends ValidationControlBase<
  TTarget,
  LicensePlateInputProps & IBindingProps<TTarget>
> {
  @observable displayTooltip = false;

  componentDidMount() {
    document.addEventListener("mousedown", this.clickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.clickOutside);
  }

  @action.bound
  clickOutside() {
    this.displayTooltip = false;
  }

  @action.bound
  showTooltip() {
    this.displayTooltip = true;
  }

  @action.bound
  hideTooltip() {
    this.displayTooltip = false;
  }

  @bound
  protected renderInner() {
    const { fixedLength = false, tooltip = true, breakpoint = "xl", ...controlProps } = this.props;
    const { translate, disabled, maxLength } = this.props;
    let className;
    if (fixedLength) {
      className = "col-12";
    } else if (breakpoint === "xxl") {
      className = "col-xxl-9";
    } else {
      className = "col-xl-6";
    }

    return (
      <Form.Group as={Col} sm={12} className={className}>
        <Form.Label>{translate("licensePlate")}</Form.Label>
        {tooltip && !disabled && (
          <Tooltip
            id="license-plate-tooltip"
            show={this.displayTooltip}
            trigger="click"
            placement={isMobile ? "auto" : "right-start"}
            onClose={this.hideTooltip}
            tooltip={translate("licensePlateTooltip")}>
            <span className="px-2">
              <Icon className="cursor-pointer" icon={IconType.MiniInfo} width={16} height={16} onClick={this.showTooltip} />
            </span>
          </Tooltip>
        )}
        {fixedLength ? (
          <LicensePlateCharacterWiseInput {...controlProps} />
        ) : (
          <LicensePlateBaseInput maxLength={maxLength} {...controlProps} />
        )}
      </Form.Group>
    );
  }
}
